import { defineStore } from 'pinia';
import { KeycloakInfo, LogRocketCode } from '@/util/env.js';
import { DOMAIN } from '@/util/env';
import { setTokenHook } from '@/util/jwt-cache';
import jwtDecode from 'jwt-decode';
import * as AccountRepo from '@/repository/account.js';
import {
	AdmAccount,
	AgrAccount,
	AutAccount,
	BaseAccount,
	InsAccount,
	Constants,
	SupAccount,
	type UserTypes,
	CountryUtil,
} from '@fibl/oxs-common';
import { useWebsiteStore } from './website';
import Keycloak from 'keycloak-js';
import EventBus from '@/util/eventbus';
import { getI18n } from '@/util/i18n';
import type { EAV } from '@fibl/zdb-common';

const keycloak = new Keycloak(KeycloakInfo);

let initPromise: Promise<boolean>;
let refreshTimer: any;

type UnknownAcc = UserTypes.UnknownAcc;

export const useTokenStore = defineStore('token', {
	state: () => ({
		sso: {
			accessToken: '',
			refreshToken: '',
			idToken: '',
			parsed: {} as Record<string, any>,
		},
		ready: false,
		isInit: false,
		main: {
			token: '',
			expires: 0,
			account: '',
			lastAccount: '',
			details: {} as UnknownAcc,
		},
		availableAccounts: [] as EAV.Entity<UnknownAcc, UserTypes.AccountCommonIndexFields>[],
	}),
	persist: {
		paths: ['main', 'sso'],
	},

	getters: {
		ssoToken(): string {
			return this.sso.accessToken;
		},
		getValidTokenForAccount() {
			return (accountId: string): string | null => {
				const ts = ~~(Date.now() / 1000);
				if (this.main.account === accountId && this.main.expires > ts) {
					return this.main.token;
				}
				return null;
			};
		},
		isGuest(): boolean {
			return this.isLoggedIn && !this.accountId;
		},
		isPreliminaryZdbToken(): boolean {
			return !this.main.account;
		},
		currentAccountName(): string {
			if (!this.main.details) return '-';
			return this.main.details.indexfields?.name as string || '';
		},
		ssoUserId(): string {
			return this.sso.parsed?.sub || '';
		},
		isLoggedIn(): boolean {
			return !!this.sso.accessToken;
		},
		accountId(): string {
			return this.main.account || '';
		},
		role(): UserTypes.RegistrationOptionName | 'GUEST' {
			if (!this.main.account) return 'GUEST';
			return this.main.details.registrationOptionName || ('GUEST' as any);
		},
		insRemoveAccessToMessage(): boolean {
			return this.role === 'ins' && this.main.details.staticData.removeAccessToMessages as boolean;
		},
		userCountries(): CountryUtil.CountryCode[] {
			if (this.role === 'agr' || this.role === 'aut' || this.role === 'con' || this.role === 'ins') {
				return [this.main.details.staticData.country as CountryUtil.CountryCode];
			}
			if (this.role === 'sup') {
				const supData = this.main.details.staticData as UserTypes.StaticDataSup;
				return supData.countrySpecific ? Object.keys(supData.countrySpecific) as CountryUtil.CountryCode[] : [];
			}
			return [];
		},
		isAgrAccountActive(): boolean {
			return !Boolean(this.main.details.staticData?.deactivationDate);
		}
	},

	actions: {
		async initApp() {
			if (!initPromise) {
				const opts: Keycloak.KeycloakInitOptions = {
					checkLoginIframe: false,
					idToken: '',
					token: '',
					refreshToken: '',
				};
				if (this.sso.accessToken) {
					opts.token = this.sso.accessToken;
					opts.checkLoginIframe = true;
					opts.onLoad = 'check-sso';
				}
				if (this.sso.idToken) opts.idToken = this.sso.idToken;
				if (this.sso.refreshToken) opts.refreshToken = this.sso.refreshToken;
				if (this.sso.accessToken) opts.token = this.sso.accessToken;
				// console.log('keycloak init', opts);
				initPromise = keycloak.init(opts);
			}
			let authenticated = false;
			try {
				authenticated = await initPromise;
			} catch (err) {
				console.warn(err);
				this.forgetTokens();
			}
			if (this.isInit) return;
			this.isInit = true;
			setTokenHook(this.getOrCreateZdbToken);
			console.log('auth init complete', { authenticated, keycloak });
			if (authenticated) {
				this.sso.accessToken = keycloak.token!;
				this.sso.parsed = keycloak.tokenParsed || {};
				this.sso.refreshToken = keycloak.refreshToken!;
				this.sso.idToken = keycloak.idToken!;
				await this.switchToFirstAvailableAccount();
			} else {
				this.forgetTokens();
			}
			this.ready = true;
			setTimeout(() => {
				document.location.hash = ''; //dirty workaround for now
			});
			if (!refreshTimer) {
				refreshTimer = setInterval(async () => {
					// console.log('refreshing?', !this.ssoToken || !this.ssoRefreshToken);
					if (!this.sso.accessToken || !this.sso.refreshToken) return;
					try {
						const refreshed = await this.refreshSsoToken(70);
						if (refreshed) {
							this.refreshToken();
						} else {
							// console.log('not refreshment needed');
						}
					} catch (e) {
						console.log('refresh error', e);
					}
				}, 1000 * 20);
			}
		},
		getCredentialsUrl() {
			return `${keycloak.createAccountUrl({ redirectUri: document.location.href })}#/credentials`;
		},
		async authenticate() {
			if (!keycloak.token) {
				const store = useWebsiteStore();
				const locale = store.locale;
				const loginUrl = await keycloak.createLoginUrl({
					redirectUri: document.location.href,
					locale,
				});
				window.location.href = loginUrl;
			}
			this.sso.accessToken = keycloak.token!;
			this.sso.parsed = keycloak.tokenParsed || {};
			this.sso.refreshToken = keycloak.refreshToken!;
			this.sso.idToken = keycloak.idToken!;
			await this.switchToFirstAvailableAccount();
		},
		async getOrCreateZdbToken() {
			if (initPromise) await initPromise;
			const i18n = getI18n();
			const now = Math.floor(Date.now() / 1000);
			// console.debug('getOrCreateZdbToken', this.main.expires - now);
			if (!this.sso.accessToken || !this.sso.refreshToken) return;
			const ssoIsValid = !!(
				this.sso.accessToken
				&& this.sso.parsed?.exp > Math.floor(Date.now() / 1000)
			);
			if (!ssoIsValid) {
				await this.refreshSsoToken(30);
			}
			if (this.main.expires && this.main.expires < now) {
				let accountId = this.main.lastAccount;
				console.debug('getting fresh token for account', accountId);
				if (!accountId) return undefined;
				const data = await AccountRepo.loginToDomain(this.sso.accessToken, accountId);
				if (!data) throw new Error(i18n.t('stores.accSwitchError'));
				this.setLogin(data.jwt);
			}
			return this.main.token;
		},

		async refreshSsoToken(minValid=10) {
			const refreshed = await keycloak.updateToken(minValid);
			if (refreshed) {
				console.debug('refreshed sso token');
				this.sso.accessToken = keycloak.token!;
				this.sso.parsed = keycloak.tokenParsed || {};
				this.sso.refreshToken = keycloak.refreshToken!;
				this.sso.idToken = keycloak.idToken!;
			}
			return refreshed;
		},

		logout() {
			this.forgetTokens();
			EventBus.emit('account-switch');
			keycloak.logout();
		},

		async refreshToken() {
			const i18n = getI18n();
			let accountId = this.main.lastAccount;
			if (!accountId) return undefined;
			const data = await AccountRepo.loginToDomain(this.sso.accessToken, accountId);
			if (!data) throw new Error(i18n.t('stores.accSwitchError'));
			this.setLogin(data.jwt);
		},

		forgetTokens() {
			this.sso.accessToken = '';
			this.sso.refreshToken = '';
			this.sso.idToken = '';
			this.sso.parsed = {};
			this.main = {
				token: '',
				expires: 0,
				account: '',
				lastAccount: this.main.lastAccount,
				details: {} as UnknownAcc,
			};
		},

		setLogin(jwt: string) {
			const i18n = getI18n();
			const { aud, sub, exp } = jwtDecode(jwt) as any;
			// console.log('setLogin', { aud, sub, exp });
			if (aud !== DOMAIN) {
				throw new Error(i18n.t('store.tokenError', { DOMAIN, aud }));
			}
			const expDate = new Date(exp * 1000);
			if (expDate < new Date()) {
				throw new Error(i18n.t('store.tokenExpired'));
			}
			this.main.token = jwt;
			this.main.expires = exp;
			this.main.account = sub;
			if (sub) this.main.lastAccount = sub;
		},

		async register() {
			const store = useWebsiteStore();
			const locale = store.locale;
			const loginUrl = await keycloak.createRegisterUrl({
				redirectUri: document.location.href,
				locale,
			});
			window.location.href = loginUrl;
		},

		async switchAccountAndLogin(accountId: string) {
			const i18n = getI18n();
			if (!accountId) throw new Error(i18n.t('store.noAccountGiven'));
			const existing = this.getValidTokenForAccount(accountId);
			if (!existing) {
				const data = await AccountRepo.loginToDomain(this.sso.accessToken, accountId);
				if (!data) throw new Error(i18n.t('store.accSwitchError'));
				this.setLogin(data.jwt);
			}
			await this.loadAccountData();
			EventBus.emit('account-switch', accountId);
		},

		modelBase(type: string) {
			switch (type) {
				case 'agr': {
					return AgrAccount;
				}
				case 'ins': {
					return InsAccount;
				}
				case 'adm': {
					return AdmAccount;
				}
				case 'aut': {
					return AutAccount;
				}
				case 'sup': {
					return SupAccount;
				}
			}
			return BaseAccount;
		},

		async loadAccountData() {
			let accountDetails: any = {};
			if (this.main.account) {
				accountDetails = await AccountRepo.getAccountDetails();
				const optName = accountDetails.registrationOptionName;
				if (!optName) {
					console.warn('invalid account', accountDetails);
					return;
				}
				if (LogRocketCode) {
					import('logrocket').then(LogRocket => {
						LogRocket.default.identify(accountDetails.id, {
							role: optName,
							name: accountDetails.indexfields?.name || ''
						});
					});
				}
			}
			this.main.details = accountDetails;
		},

		getAccountModel() {
			const optName = this.main.details.registrationOptionName;
			const accountDetails = this.main.details;
			const base = this.modelBase(optName);
			let accountModel = new base();
			accountModel.fromStorage(JSON.parse(JSON.stringify(accountDetails)));
			return accountModel;
		},

		async loadAvailableAccounts() {
			const entities = (await AccountRepo.listAccountsForUser(this.ssoUserId));
			const store = useWebsiteStore();
			const roles: UserTypes.RegistrationOptionName[] =
				store.websiteCode === Constants.WEB_ADMIN
					? ['adm']
					: store.websiteCode === Constants.WEB_RDB
					? ['aut', 'sup']
					: ['agr', 'aut', 'ins', 'con'];
			this.availableAccounts = entities.filter((e) => {
				if (roles.length === 0) return true;
				return roles.includes(e.data.registrationOptionName);
			});
		},

		async switchToFirstAvailableAccount() {
			await this.loadAvailableAccounts();
			if (this.availableAccounts.length > 0) {
				if (
					this.main.lastAccount &&
					this.availableAccounts.find((a) => a.id === this.main.lastAccount)
				) {
					await this.switchAccountAndLogin(this.main.lastAccount);
				} else {
					await this.switchAccountAndLogin(this.availableAccounts[0].id);
				}
			}
		},

		async registerWithInvite({
			inviteId,
			registrationName,
		}: {
			inviteId: string;
			registrationName: string;
		}) {
			// await dispatch('auth/token/initApp', null, { root: true });
			let token = this.sso.accessToken;
			let accountId = await AccountRepo.registerWithDomainByInvite(
				inviteId,
				registrationName,
				token,
			);
			this.switchAccountAndLogin(accountId);
		},

		async updateMyUserData(userData: Record<string,any>, surpressError?: boolean) {
			const newAccount = await AccountRepo.updateAccount(userData, surpressError);
			this.main.details.userData = newAccount.userData;
		}
	},
});
